import { BackgroundColor } from '@/theme/types';
import { cn } from '@/shadcn-ui/utils';
import { type ClassValue } from 'clsx';
import Header, { HeaderProps } from '@/common/components/header/header';
import Spacer, { SpacerVariant } from '@/common/components/spacer';
import LiveLargeFooter from './live-large-footer/live-large-footer';
import { useFeatureFlags } from '@/util-react-router/hooks/use-feature-flags';

/**
 * Helper function to apply page padding, to be used in page layouts throughout the app.
 *
 * @param {string} className - Additional CSS classes to apply.
 * @returns {string} The combined CSS classes.
 */
export function withPageContainerStyles(...inputs: ClassValue[]) {
  return cn('flex w-full flex-col justify-center px-[24px]', inputs);
}

interface PageLayoutProps extends HeaderProps {
  /**
   * Ref to the page layout container
   */
  ref?: React.RefObject<HTMLDivElement | null>;
  children: React.ReactNode;
  /**
   * Banner that goes below header and expands entire width of rendered page
   */
  banner?: React.ReactNode;
  /**
   * Disable PageContainer within PageLayout. If true, the PageContainer styles
   * won't be applied to the children of PageLayout. This gives you more control
   * over the layout of your page. You can then use the PageContainer functiondirectly to apply the styles where you want them.
   */
  disablePageContainer?: boolean;
  /**
   * Disable the margin between footer and content.
   */
  disableFooterMargin?: boolean;
  /**
   * Set the background color of the page.
   */
  backgroundColor?: BackgroundColor;
  /**
   * Set the variant for the spacer that seperates the header & content.
   */
  headerContentSpacerVariant?: SpacerVariant | 'no-spacer';
  /** Feed left hand content to header nav bar */
  headerLeftContent?: React.ReactNode;
  /** Class name for header */
  headerClassName?: string;
  /** Class name to amend page container styles */
  pageContainerClassName?: string;
  /** Ref to header */
  headerRef?: React.RefObject<HTMLDivElement | null>;
  /** Class name for footer */
  footerClassName?: string;
}

/**
 * Component that provides a consistent layout for pages.
 */
export default function PageLayout({
  ref,
  children,
  dark,
  banner,
  disablePageContainer = false,
  disableFooterMargin = false,
  backgroundColor = 'bg-flexcar-neutral100',
  headerSheetBackgroundColor,
  headerContentSpacerVariant = 'height32',
  // Header
  headerClassName,
  hideLogo,
  hideHomeLink,
  hideCatalogLink,
  headerLeftContent,
  logoFillColor,
  navLinkClassName,
  hamburgerFillPrimary,
  compactMobile,
  to,
  pageContainerClassName,
  // Footer
  footerClassName,
}: PageLayoutProps) {
  const { OSANO_ENABLED } = useFeatureFlags();
  return (
    <div
      ref={ref}
      className={cn(
        'flex min-h-screen w-full flex-col items-center justify-between',
        backgroundColor
      )}
    >
      <div className="flex w-full flex-col items-center justify-center">
        <div
          className={withPageContainerStyles('max-w-[1440px]', pageContainerClassName, {
            'px-0 md:px-6': compactMobile,
            'pr-[24px]': !compactMobile && !hideCatalogLink,
            // Adjusts padding when we hide catalog link so that visually the link is still flush with the right edge of the page container but we maintain the larger clickable area of the last nav link item
            'pr-[8px]': !compactMobile && hideCatalogLink,
          })}
        >
          <Header
            className={headerClassName}
            dark={dark}
            hideLogo={hideLogo}
            hideHomeLink={hideHomeLink}
            hideCatalogLink={hideCatalogLink}
            // Header Sheet
            backgroundColor={backgroundColor}
            headerSheetBackgroundColor={headerSheetBackgroundColor}
            leftContent={headerLeftContent}
            logoFillColor={logoFillColor}
            navLinkClassName={navLinkClassName}
            hamburgerFillPrimary={hamburgerFillPrimary}
            compactMobile={compactMobile}
            to={to}
          />
        </div>

        {banner ? <div className="w-full">{banner}</div> : null}

        <div
          className={withPageContainerStyles('max-w-[1440px]', pageContainerClassName)}
        >
          {!disablePageContainer ? (
            <>
              {headerContentSpacerVariant !== 'no-spacer' ? (
                <Spacer variant={headerContentSpacerVariant} />
              ) : null}
              {children}
            </>
          ) : null}
        </div>

        {disablePageContainer ? (
          <>
            {headerContentSpacerVariant !== 'no-spacer' ? (
              <Spacer variant={headerContentSpacerVariant} />
            ) : null}
            {children}
          </>
        ) : null}
      </div>

      <div
        className={cn(
          'flex w-full flex-col items-center justify-center border-t border-flexcar-neutral80 bg-flexcar-neutral100',
          {
            'mt-[160px]': !disableFooterMargin,
          }
        )}
      >
        <div
          className={withPageContainerStyles(
            'max-w-[1440px] pb-[24px] pt-[88px] md:pt-[120px]',
            footerClassName
          )}
        >
          <LiveLargeFooter osanoEnabled={OSANO_ENABLED} />
        </div>
      </div>
    </div>
  );
}
